import axios from "axios";
import { endpoint } from "../config";
import {
  clearCookieData,
  getJWTTokenFromCookie,
  getRawJWTTokenFromCookie,
} from "../utils";

const DEFAULT_SESSION_TIMEOUT_MS = 15 * 60 * 1000;
const DEFAULT_SESSION_HEARTBEAT_INTERVAL_MS = 60 * 1000;
const SESSION_TIMEOUT_STORAGE_KEY = "mcsportal_session_timeout_ms";
const SESSION_HEARTBEAT_INTERVAL_STORAGE_KEY =
  "mcsportal_session_heartbeat_interval_ms";
const getMaxHeartbeatIntervalMs = (timeoutMs) =>
  Math.max(1000, Math.floor(timeoutMs / 2));

const getPositiveNumber = (value, fallbackValue) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallbackValue;
};

const normalizeSessionHeartbeatIntervalMs = (value, timeoutMs) => {
  const normalizedTimeoutMs = getPositiveNumber(
    timeoutMs,
    DEFAULT_SESSION_TIMEOUT_MS
  );
  const normalizedIntervalMs = getPositiveNumber(
    value,
    DEFAULT_SESSION_HEARTBEAT_INTERVAL_MS
  );

  return Math.min(
    normalizedIntervalMs,
    getMaxHeartbeatIntervalMs(normalizedTimeoutMs)
  );
};

const sessionEndpoint = `${endpoint}/api/session`;

const getSessionIdFromToken = () => {
  const rawToken = getRawJWTTokenFromCookie();

  if (!rawToken) {
    return null;
  }

  try {
    const tokenPayload = JSON.parse(atob(rawToken.split(".")[1]));
    return tokenPayload.sid || null;
  } catch (error) {
    return null;
  }
};

const hasTrackableSession = () =>
  Boolean(getJWTTokenFromCookie()) && Boolean(getSessionIdFromToken());

const postSessionUpdate = async (path, data = {}) => {
  if (!hasTrackableSession()) {
    return null;
  }

  return axios.post(`${sessionEndpoint}${path}`, data);
};

const getStoredSessionTimeoutMs = () => {
  try {
    return getPositiveNumber(
      window.localStorage.getItem(SESSION_TIMEOUT_STORAGE_KEY),
      DEFAULT_SESSION_TIMEOUT_MS
    );
  } catch (error) {
    return DEFAULT_SESSION_TIMEOUT_MS;
  }
};

const setStoredSessionTimeoutMs = (value) => {
  const normalizedValue = getPositiveNumber(value, DEFAULT_SESSION_TIMEOUT_MS);

  try {
    window.localStorage.setItem(
      SESSION_TIMEOUT_STORAGE_KEY,
      String(normalizedValue)
    );
  } catch (error) {
  }

  return normalizedValue;
};

const getStoredSessionHeartbeatIntervalMs = () => {
  try {
    return normalizeSessionHeartbeatIntervalMs(
      window.localStorage.getItem(SESSION_HEARTBEAT_INTERVAL_STORAGE_KEY),
      getStoredSessionTimeoutMs()
    );
  } catch (error) {
    return normalizeSessionHeartbeatIntervalMs(
      DEFAULT_SESSION_HEARTBEAT_INTERVAL_MS,
      getStoredSessionTimeoutMs()
    );
  }
};

const setStoredSessionHeartbeatIntervalMs = (value, timeoutMs) => {
  const normalizedValue = normalizeSessionHeartbeatIntervalMs(
    value,
    timeoutMs
  );

  try {
    window.localStorage.setItem(
      SESSION_HEARTBEAT_INTERVAL_STORAGE_KEY,
      String(normalizedValue)
    );
  } catch (error) {
  }

  return normalizedValue;
};

const fetchSessionConfig = async () => {
  if (!Boolean(getJWTTokenFromCookie())) {
    return {
      sessionHeartbeatIntervalMs: getStoredSessionHeartbeatIntervalMs(),
      sessionTimeoutMs: getStoredSessionTimeoutMs(),
    };
  }

  const response = await axios.get(`${sessionEndpoint}/config`);
  const sessionTimeoutMs = setStoredSessionTimeoutMs(
    response.data?.sessionTimeoutMs
  );
  const sessionHeartbeatIntervalMs = setStoredSessionHeartbeatIntervalMs(
    response.data?.sessionHeartbeatIntervalMs,
    sessionTimeoutMs
  );

  return {
    sessionHeartbeatIntervalMs,
    sessionTimeoutMs,
  };
};

const sendSessionHeartbeat = async () => postSessionUpdate("/heartbeat");

const startSessionActivity = async ({ categoryId, visualizationId }) =>
  postSessionUpdate("/activity/start", { categoryId, visualizationId });

const stopSessionActivity = async () => postSessionUpdate("/activity/stop");

const logoutCurrentSession = async (reason = "manual_logout") =>
  postSessionUpdate("/logout", { reason });

const clearClientSession = () => {
  clearCookieData();
  window.location = "/";
};

const forceClientLogout = async (reason = "manual_logout") => {
  try {
    await logoutCurrentSession(reason);
  } catch (error) {
  } finally {
    clearClientSession();
  }
};

const sendSessionLogoutBeacon = (reason = "browser_closed") => {
  const token = getRawJWTTokenFromCookie();
  const sessionId = getSessionIdFromToken();

  if (!token || !sessionId || !endpoint) {
    return false;
  }

  const payload = JSON.stringify({ reason, token });
  const beaconUrl = `${sessionEndpoint}/logout/beacon`;

  if (navigator.sendBeacon) {
    return navigator.sendBeacon(
      beaconUrl,
      new Blob([payload], { type: "application/json" })
    );
  }

  fetch(beaconUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: payload,
    keepalive: true,
  }).catch(() => {});

  return true;
};

export {
  clearClientSession,
  fetchSessionConfig,
  forceClientLogout,
  getSessionIdFromToken,
  getStoredSessionHeartbeatIntervalMs,
  getStoredSessionTimeoutMs,
  logoutCurrentSession,
  sendSessionHeartbeat,
  sendSessionLogoutBeacon,
  setStoredSessionTimeoutMs,
  startSessionActivity,
  stopSessionActivity,
};
